import { useQuery } from '@tanstack/react-query';
import { http } from 'services/http';
import { TRIPS_GET_MANY, TRIPS_SEARCH_HINTS } from 'api/query-keys.ts';
import type { TGetTripRes } from 'api/swagger';
import { PAGINATION_DEFAULT_LIMIT } from 'shared/constants/paginations';
import type { ITripFilter } from 'shared/stores/use-trip-filters.ts';
import { setFiltersToQs } from 'shared/utils/filters/set-filters-to-qs.ts';
import type { WithPagination } from 'shared/utils/pagination.ts';
import { preparePagination } from 'shared/utils/pagination.ts';
import type { IWithPagination } from 'types/api-query.ts';

interface IParams extends IWithPagination {
  search?: string;
  filters?: ITripFilter;
  isSkip?: boolean;
  isHints?: boolean;
}

export const useGetManyTrips = (params?: IParams) => {
  const { limit = PAGINATION_DEFAULT_LIMIT, page = 1, search, filters, isSkip = false, isHints = false } = params || {};

  const qs = new URLSearchParams({ limit: String(limit), page: String(page) });
  if (search) qs.set('filter[search]', search);
  if (filters) setFiltersToQs<ITripFilter>(qs, filters);

  return useQuery<WithPagination<TGetTripRes[]>>({
    queryKey: isHints ? [TRIPS_SEARCH_HINTS, search] : [TRIPS_GET_MANY, page, limit, qs.toString()],
    queryFn: async () => {
      const { data, headers } = await http.get<TGetTripRes[]>(`/v1/trips?${qs.toString()}`);
      const pagination = preparePagination(headers);

      return { data, pagination };
    },
    retry: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    staleTime: 0,
    cacheTime: 0,
    enabled: !isSkip,
  });
};
